export function normalizeNestingCode(value) {
    return String(value || "").trim().toUpperCase();
}

export function isPotentialNestingQuery(value) {
    const query = normalizeNestingCode(value);
    return /^(J\d{2,}|\d{5,})/.test(query);
}

export function nestingSearchHit(item = {}) {
    const code = normalizeNestingCode(item.codice || item.job_id);
    return {
        ...item,
        codice: code,
        articolo: "",
        route: "laser-nesting-detail",
        searchType: "nesting",
        descrizione: [
            item.stato_label,
            item.materiale,
            Number(item.fogli || 0) ? `${item.fogli} fogli` : null,
            Number(item.odp || 0) ? `${item.odp} ODP` : null,
        ].filter(Boolean).join(" · "),
    };
}

function validDateValue(value) {
    if (!value) return null;
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? { value, timestamp } : null;
}

function boundaryDate(values, direction) {
    const dates = values.map(validDateValue).filter(Boolean);
    if (!dates.length) return null;
    return dates.reduce((selected, candidate) => (
        direction === "first"
            ? (candidate.timestamp < selected.timestamp ? candidate : selected)
            : (candidate.timestamp > selected.timestamp ? candidate : selected)
    )).value;
}

export function nestingCutWindow(job = {}) {
    const sheets = Array.isArray(job.job_sheets) ? job.job_sheets : [];
    const sheetStarts = sheets.map((sheet) => sheet.report_first_start_time);
    const sheetEnds = sheets.map((sheet) => sheet.report_last_end_time);
    const completed = Boolean(job.is_completed || job.end_time);
    return {
        start: boundaryDate(sheetStarts, "first") || validDateValue(job.start_time)?.value || null,
        end: completed ? boundaryDate([...sheetEnds, job.end_time], "last") : null,
    };
}

export function formatNestingDateTime(value) {
    const parsed = validDateValue(value);
    if (!parsed) return "Non disponibile";
    return new Date(parsed.timestamp).toLocaleString("it-IT", {
        dateStyle: "short",
        timeStyle: "short",
    });
}

function number(value) {
    const parsed = Number(value || 0);
    return Number.isFinite(parsed) ? parsed : 0;
}

function normalizedOrderNumber(value) {
    return normalizeNestingCode(value).replace(/^RODP/, "ODP").split("_")[0];
}

function sheetProgress(sheet = {}) {
    const planned = Math.max(0, number(sheet.no_of_runs || sheet.total_no_of_runs));
    const completed = Math.max(0, number(sheet.completed_runs));
    const started = number(sheet.report_started_runs) > 0
        || completed > 0
        || [1, 2, 3].includes(Number(sheet.current_state));
    return {
        planned,
        completed,
        started,
        completedSheet: Boolean(sheet.done) || (planned > 0 && completed >= planned),
    };
}

function orderState(group) {
    if (group.sbancalato || (group.tglPlanned > 0 && group.sbcCompleted >= group.tglPlanned)) {
        return { value: "completed", label: "Completato SBC", color: "success", icon: "mdi-check-circle" };
    }
    if (group.allSheetsCut || group.versatoGestionale) {
        return { value: "cut", label: "Tagliato", color: "teal", icon: "mdi-content-cut" };
    }
    if (group.anySheetStarted) {
        return { value: "cutting", label: "In taglio", color: "info", icon: "mdi-progress-clock" };
    }
    return { value: "pending", label: "Da tagliare", color: "blue-grey", icon: "mdi-clock-outline" };
}

export function buildNestingOrderRows(sheets = []) {
    const groups = new Map();
    (sheets || []).forEach((sheet) => {
        const progress = sheetProgress(sheet);
        const program = String(sheet.program_no || sheet.manufacturing_order_no || "").trim();
        (sheet.orders || []).forEach((order, index) => {
            const odp = normalizedOrderNumber(order.production_order_no) || `ODP-${sheet.id || "foglio"}-${index}`;
            if (!groups.has(odp)) {
                groups.set(odp, {
                    odp,
                    quantity: 0,
                    producedQuantity: 0,
                    articles: new Set(),
                    programs: new Set(),
                    tglPlanned: 0,
                    tglCompleted: 0,
                    sbcCompleted: 0,
                    quantitaVersata: 0,
                    versatoGestionale: false,
                    sbancalato: false,
                    phaseProgress: [],
                    completedOperations: null,
                    totalOperations: null,
                    phaseProgressAvailable: false,
                    anySheetStarted: false,
                    allSheetsCut: true,
                    scheduledDate: null,
                    lastCutDate: null,
                });
            }
            const group = groups.get(odp);
            group.quantity += number(order.total_quantity_in_job);
            group.producedQuantity += number(order.produced_quantity);
            group.tglPlanned = Math.max(group.tglPlanned, number(order.tgl_planned_quantity));
            group.tglCompleted = Math.max(group.tglCompleted, number(order.tgl_completed_quantity));
            group.sbcCompleted = Math.max(group.sbcCompleted, number(order.sbc_completed_quantity));
            group.quantitaVersata = Math.max(group.quantitaVersata, number(order.quantita_versata));
            group.versatoGestionale = group.versatoGestionale || Boolean(order.versato_gestionale);
            group.sbancalato = group.sbancalato || Boolean(order.sbancalato);
            const phaseProgress = Array.isArray(order.phase_progress) ? order.phase_progress : [];
            const totalOperations = Number(order.tot_ope);
            const completedOperations = Number(order.ope_saldate);
            const phaseProgressAvailable = Boolean(order.phase_progress_available)
                && Number.isFinite(totalOperations)
                && totalOperations > 0;
            if (
                phaseProgressAvailable
                && (!group.phaseProgressAvailable || totalOperations > group.totalOperations)
            ) {
                group.phaseProgress = phaseProgress;
                group.completedOperations = Number.isFinite(completedOperations) ? completedOperations : 0;
                group.totalOperations = totalOperations;
                group.phaseProgressAvailable = true;
            }
            group.anySheetStarted = group.anySheetStarted || progress.started;
            group.allSheetsCut = group.allSheetsCut && progress.completedSheet;
            if (program) group.programs.add(program);
            const article = String(order.art || order.part_drawing_no || order.part_no || "").trim();
            if (article) group.articles.add(article);
            group.scheduledDate = group.scheduledDate || order.scheduled_cut_date || order.order_due_date || null;
            group.lastCutDate = order.sbancalato_data
                || order.data_versamento
                || sheet.report_last_end_time
                || group.lastCutDate;
        });
    });

    return [...groups.values()].map((group) => ({
        ...group,
        articles: [...group.articles],
        programs: [...group.programs],
        state: orderState(group),
    })).sort((left, right) => left.odp.localeCompare(right.odp, "it", { numeric: true }));
}
